// @ts-nocheck
import { addToCart, waitForAmplitude } from "@theme/utilities";
import { getPageGlobalProps } from "@theme/analytics.util";
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
/**
 * Initialize BODi button click handlers
 */
(function initBodiButtons() {
  const namespace = "[BODiButton]";
  const ERROR_DISPLAY_DURATION = 10000; // 10 seconds
  const errorTimeouts = new Map();

  /**
   * Handle navigate action
   */
  async function handleNavigate(button) {
    const url = button.dataset.url;
    const openNewTab = button.dataset.openNewTab === "true";

    if (!url) {
      console.warn(`${namespace} - Navigate action requires URL`);
      return;
    }

    await trackCtaAnalytics(button, { "cta url": url });

    if (openNewTab) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = url;
    }
  }

  /**
   * Handle add items to cart action
   */
  async function handleAddItems(button) {
    const variantIdsStr = button.dataset.variantIds;
    const sellingPlanIdsStr = button.dataset.sellingPlanIds || "";
    const quantitiesStr = button.dataset.quantities || "";
    const redirectUrl = button.dataset.redirectUrl;
    const analyticsModule = button.dataset.analyticsModule || "unknown";

    if (!variantIdsStr) {
      console.warn(`${namespace} - Add items action requires variant IDs`);
      return;
    }

    const analyticsProps = redirectUrl ? { "cta url": redirectUrl } : {};
    await trackCtaAnalytics(button, analyticsProps);

    try {
      // Parse variant IDs
      const variantIds = variantIdsStr
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      if (variantIds.length === 0) {
        console.warn(`${namespace} - No valid variant IDs provided`);
        return;
      }

      // Parse selling plan IDs (single value applies to all)
      const sellingPlanIds = parseCommaSeparatedValues(sellingPlanIdsStr, variantIds.length);

      // Parse quantities (single value applies to all, default to 1)
      const quantities = parseCommaSeparatedValues(quantitiesStr, variantIds.length);
      const finalQuantities =
        quantities.length > 0
          ? quantities.map((q) => parseInt(q, 10) || 1)
          : Array(variantIds.length).fill(1);

      // Build items array for cart API
      const items = variantIds.map((variantId, index) => {
        const item = {
          id: variantId,
          quantity: finalQuantities[index] || 1,
        };

        // Add selling plan if provided
        if (sellingPlanIds[index]) {
          item.selling_plan = sellingPlanIds[index];
        }

        return item;
      });

      console.info(`${namespace} - Adding items to cart:`, items);

      // Hide any previous error
      hideError(button);

      // Add to cart and get response
      let cartResponse;
      try {
        cartResponse = await addToCart(items, {
          analyticsModule,
          redirectUrl,
        });
      } catch (cartError) {
        console.error(`${namespace} - Cart API error:`, cartError);
        const errorMessage = cartError?.data?.message || cartError?.message;
        showError(button, errorMessage);
        return;
      }

      // Redirect if specified (delay to allow tracking events to fire)
      if (redirectUrl) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error(`${namespace} - Failed to add items to cart`, { error });
      const errorMessage = error?.message || "Failed to add items to cart";
      showError(button, errorMessage);
    }
  }

  /**
   * Handle open modal action
   * Opens a modal by emitting a custom event
   */
  async function handleOpenModal(button) {
    const modalId = button.dataset.modalId;

    if (!modalId) {
      console.warn(`${namespace} - Open modal action requires modal ID`);
      return;
    }

    await trackCtaAnalytics(button, { "modal id": modalId });

    window.dispatchEvent(
      new CustomEvent("bodi:openModal", {
        detail: { modalId },
      })
    );
  }

  /**
   * Handle open chatbot action
   * Opens the Sierra chatbot modal using hash navigation
   */
  async function handleOpenChatbot(button) {
    await trackCtaAnalytics(button, { action: "open chatbot" });

    // Use hash navigation to open chat
    const url = new URL(window.location.href);
    url.hash = "chat";
    history.replaceState(null, "", url);
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  }

  /**
   * Handle add PDP item to cart action
   * Reuses the existing product form submission logic from product-form.js
   * This triggers the same behavior as the add-to-cart.liquid button
   */
  async function handleAddPdpItem(button) {
    const redirectUrl = button.dataset.redirectUrl;
    const analyticsModule = button.dataset.analyticsModule || "unknown";

    const analyticsProps = redirectUrl ? { "cta url": redirectUrl } : {};
    await trackCtaAnalytics(button, analyticsProps);

    try {
      // Find the product form component (same as add-to-cart.liquid uses)
      const productFormComponent = document.querySelector("product-form-component");

      if (!productFormComponent) {
        console.warn(
          `${namespace} - Product form component not found. This action only works on product pages.`
        );
        return;
      }

      const productForm = productFormComponent.querySelector("form");

      if (!productForm) {
        console.warn(`${namespace} - Product form not found inside component.`);
        return;
      }

      // Store analytics info for the product form to use
      productFormComponent.dataset.analyticsModule = analyticsModule;
      productFormComponent.dataset.redirectUrl = redirectUrl;

      // Fetch product JSON to resolve the selling plan for the selected variant
      const variantInput = productForm.querySelector('input[name="id"]');

      if (variantInput) {
        try {
          const productUrl = productFormComponent.dataset.productUrl || window.location.pathname;
          const productJsonUrl = new URL(productUrl, window.location.origin);
          productJsonUrl.pathname = `${productUrl}.js`;

          const response = await fetch(productJsonUrl.toString());
          if (response.ok) {
            const productData = await response.json();
            const selectedVariantId = String(variantInput.value);
            const variant = productData.variants.find(
              (v) => String(v.id) === selectedVariantId
            );
            const sellingPlanId =
              variant?.selling_plan_allocations?.[0]?.selling_plan_id;

            let sellingPlanInput = productForm.querySelector('input[name="selling_plan"]');

            if (!sellingPlanInput) {
              sellingPlanInput = document.createElement("input");
              sellingPlanInput.type = "hidden";
              sellingPlanInput.name = "selling_plan";
              productForm.appendChild(sellingPlanInput);
            }

            sellingPlanInput.value = sellingPlanId || "";
          }
        } catch (fetchError) {
          console.error(`${namespace} - Failed to fetch product data for selling plan`, {
            error: fetchError,
          });
        }
      }

      productForm.requestSubmit();

      if (redirectUrl) {
        // Wait for cart to update and tracking to complete
        await new Promise((resolve) => setTimeout(resolve, 800));
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error(`${namespace} - Failed to add PDP item to cart`, { error });
      const errorMessage = error?.data?.message || error?.message || "Failed to add item to cart";
      showError(button, errorMessage);
    }
  }

  async function handleAddSkuItem(button) {
    const variantIdsStr = button.dataset.variantIds;
    const sellingPlanIdsStr = button.dataset.sellingPlanIds || "";
    const quantitiesStr = button.dataset.quantities || "";
    const redirectUrl = button.dataset.redirectUrl;
    const analyticsModule = button.dataset.analyticsModule || "unknown";
    const skuSelector = button.closest("[data-sku-dropdown]") || document.querySelector("[data-sku-dropdown]");
      
      if (!skuSelector) {
        console.warn(`${namespace} - SKU dropdown not found.`);
        showError(button, "SKU dropdown not found");
        return;
      }
      
      const selectedOption = skuSelector.querySelector(".sku-dropdown__option.is-selected");
      
      if (!selectedOption) {
        console.warn(`${namespace} - No SKU option selected.`);
        showError(button, "Please select a SKU option");
        return;
      }
      
      // Extract the variant ID and selling plan ID from the selected option
      const selectedVariantId = selectedOption.dataset.variant;
      const selectedSellingPlanId = selectedOption.dataset.sellingPlan;
      
    
    try {
      const variantIds = [selectedVariantId];

      if (variantIds.length === 0) {
        console.warn(`${namespace} - No valid variant IDs provided`);
        return;
      }
      
      // Parse selling plan IDs (single value applies to all)
      const sellingPlanIds = parseCommaSeparatedValues(sellingPlanIdsStr, variantIds.length);
      
      const quantities = parseCommaSeparatedValues(quantitiesStr, variantIds.length);
      const finalQuantities = quantities.length > 0
        ? quantities.map((q) => parseInt(q, 10) || 1)
        : Array(variantIds.length).fill(1);
      
      // Build items array for cart API
      const items = variantIds.map((variantId, index) => {
        const item = {
          id: variantId,
          quantity: finalQuantities[index] || 1,
        };
        
        // Use the selected selling plan ID from the SKU option
        if (selectedSellingPlanId) {
          item.selling_plan = selectedSellingPlanId;
        }
        
        return item;
      });
      
      console.info(`${namespace} - Adding items to cart:`, items);
      
      // Hide any previous error
      hideError(button);
      
      // Add to cart and get response
      let cartResponse;
      try {
        cartResponse = await addToCart(items);
      } catch (cartError) {
        console.error(`${namespace} - Cart API error:`, cartError);
        const errorMessage = cartError?.data?.message || cartError?.message;
        showError(button, errorMessage);
        return;
      }
      
      // Track analytics
      await trackAddToCartAnalytics({
        cartItems: cartResponse.items,
        redirectUrl,
        analyticsModule,
      });
      
      // Redirect if specified
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error(`${namespace} - Failed to add items to cart`, { error });
      const errorMessage = error?.message || "Failed to add items to cart";
      showError(button, errorMessage);
    }
  }

  async function handleButtonClick(event) {
    const button = event.currentTarget;
    const clickAction = button.dataset.clickAction;

    if (!clickAction || clickAction === "none") {
      return;
    }

    if (button.tagName === "BUTTON") {
      event.preventDefault();
    }

    button.classList.add("bodi-button--loading");
    button.disabled = true;

    try {
      switch (clickAction) {
        case "navigate":
          await handleNavigate(button);
          break;
        case "open_modal":
          await handleOpenModal(button);
          break;
        case "open_chatbot":
          await handleOpenChatbot(button);
          break;
        case "add_items":
          await handleAddItems(button);
          break;
        case "add_pdp_item":
          await handleAddPdpItem(button);
          break;
        case "add_sku_item":
          await handleAddSkuItem(button);
          break;
        default:
          console.warn(`${namespace} - Unknown click action: ${clickAction}`);
      }
    } catch (error) {
      console.error(`${namespace} - Error handling click action`, { error, clickAction });
    } finally {
      button.classList.remove("bodi-button--loading");
      button.disabled = false;
    }
  }

  /**
   * Track analytics event for button click
   */
  async function trackAnalyticsEvent(eventName, properties) {
    try {
      await waitForAmplitude();

      const eventProperties = {
        ...getPageGlobalProps(window.location.href),
        ...properties,
      };

      await window.amplitude.track(eventName, eventProperties);
      await window.amplitude.flush();
      console.info(`${namespace} - Tracked ${eventName}`, eventProperties);
    } catch (error) {
      console.error(`${namespace} - Failed to track ${eventName}`, { error, properties });
    }
  }

  /**
   * Track CTA analytics event (shared by navigate and open_modal actions)
   * @param {HTMLElement} button - The button element
   * @param {Object} additionalProps - Additional properties to include in the event
   */
  async function trackCtaAnalytics(button, additionalProps = {}) {
    const analyticsEvent = button.dataset.analyticsEvent;
    const analyticsModule = button.dataset.analyticsModule || "unknown";

    if (!analyticsEvent || analyticsEvent === "none") return;

    const eventName = analyticsEvent.replace(/\s+/g, " ").toLowerCase();

    const payload = {
      module: analyticsModule,
      ...additionalProps,
    };

    // Add rail-specific properties for selectRailCta event
    if (analyticsEvent === "select rail cta") {
      payload["rail slug"] = button.dataset.railSlug || "unknown";
      payload["cta position"] = parseInt(button.dataset.ctaPosition, 10) || 1;
    }

    await trackAnalyticsEvent(eventName, payload);
  }

  /**
   * Parse comma-separated values, applying single value to all if only one provided
   */
  function parseCommaSeparatedValues(value, count) {
    if (!value || value.trim() === "") return [];

    const values = value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

    // If only one value provided, apply to all items
    if (values.length === 1 && count > 1) {
      return Array(count).fill(values[0]);
    }

    return values;
  }

  function showError(button, message) {
    const wrapper = button.closest(".bodi-button-wrapper");
    if (!wrapper) return;

    const errorElement = wrapper.querySelector(".bodi-button__error");
    const errorTextElement = wrapper.querySelector(".bodi-button__error-text");

    if (!errorElement || !errorTextElement) return;

    // Clear any existing timeout for this button
    const existingTimeout = errorTimeouts.get(button);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Show error message
    errorTextElement.textContent = message;
    errorElement.classList.remove("hidden");

    console.error(`${namespace} - Error: ${message}`);

    // Auto-hide after duration
    const timeout = setTimeout(() => {
      hideError(button);
    }, ERROR_DISPLAY_DURATION);

    errorTimeouts.set(button, timeout);
  }

  function hideError(button) {
    const wrapper = button.closest(".bodi-button-wrapper");
    if (!wrapper) return;

    const errorElement = wrapper.querySelector(".bodi-button__error");
    const errorTextElement = wrapper.querySelector(".bodi-button__error-text");

    if (errorElement) {
      errorElement.classList.add("hidden");
    }
    if (errorTextElement) {
      errorTextElement.textContent = "";
    }

    const existingTimeout = errorTimeouts.get(button);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      errorTimeouts.delete(button);
    }
  }

  function initButtons() {
    const buttons = document.querySelectorAll(".bodi-button[data-click-action]");

    buttons.forEach((button) => {
      button.removeEventListener("click", handleButtonClick);
      button.addEventListener("click", handleButtonClick);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initButtons);
  } else {
    initButtons();
  }

  const observer = new MutationObserver(() => {
    initButtons();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  
})();
